import { zeroErrorMessage } from "@/lib/zero-error";
import { useQuery, useZero } from "@rocicorp/zero/react";
import { appMutators, appQueries } from "@production-hub/shared";
import { toast } from "sonner";
import type {
  Tables,
  TablesInsert,
  TablesUpdate,
} from "@/integrations/neon/types";
import { generateUUID } from "@/lib/utils";
import {
  reportServerMutationFailure,
  toLegacyTimestamp,
  usePendingMutation,
  type MutationOptions,
  type ZeroMutateClient,
} from "./useZeroMutationUtils";

export type CloudBroker = Tables<"brokers">;
export type CloudBrokerInsert = TablesInsert<"brokers">;
export type CloudBrokerUpdate = TablesUpdate<"brokers">;
export type CloudBrokerOutput = Tables<"broker_outputs">;
export type CloudBrokerOutputInsert = TablesInsert<"broker_outputs">;
export type CloudBrokerOutputUpdate = TablesUpdate<"broker_outputs">;

type ZeroBroker = {
  id: string;
  studioID: string;
  name: string;
  brokerType: string;
  config?: CloudBroker["config"];
  enabled: boolean;
  createdAt?: number | string | null;
  updatedAt?: number | string | null;
};

type ZeroBrokerOutput = {
  id: string;
  brokerID: string;
  name: string;
  outputType: string;
  config?: CloudBrokerOutput["config"];
  enabled: boolean;
  sortOrder: number;
  createdAt?: number | string | null;
  updatedAt?: number | string | null;
};

function toLegacyBroker(row: ZeroBroker): CloudBroker {
  return {
    id: row.id,
    studio_id: row.studioID,
    name: row.name,
    broker_type: row.brokerType,
    config: row.config ?? {},
    enabled: row.enabled,
    created_at: toLegacyTimestamp(row.createdAt),
    updated_at: toLegacyTimestamp(row.updatedAt),
  };
}

function toLegacyBrokerOutput(row: ZeroBrokerOutput): CloudBrokerOutput {
  return {
    id: row.id,
    broker_id: row.brokerID,
    name: row.name,
    output_type: row.outputType,
    config: row.config ?? {},
    enabled: row.enabled,
    sort_order: row.sortOrder,
    created_at: toLegacyTimestamp(row.createdAt),
    updated_at: toLegacyTimestamp(row.updatedAt),
  };
}

function toZeroBrokerInput(broker: CloudBrokerInsert) {
  return {
    id: broker.id ?? generateUUID(),
    studioID: broker.studio_id,
    name: broker.name,
    brokerType: broker.broker_type,
    config: broker.config ?? {},
    enabled: broker.enabled ?? true,
  };
}

function toZeroBrokerUpdate(
  id: string,
  studioID: string,
  broker: CloudBrokerUpdate,
) {
  return {
    id,
    studioID,
    ...(broker.name !== undefined ? { name: broker.name } : {}),
    ...(broker.broker_type !== undefined
      ? { brokerType: broker.broker_type }
      : {}),
    ...(broker.config !== undefined ? { config: broker.config } : {}),
    ...(broker.enabled !== undefined ? { enabled: broker.enabled } : {}),
  };
}

function toZeroBrokerOutputInput(output: CloudBrokerOutputInsert) {
  return {
    id: output.id ?? generateUUID(),
    brokerID: output.broker_id,
    name: output.name,
    outputType: output.output_type,
    config: output.config ?? {},
    enabled: output.enabled ?? true,
    sortOrder: output.sort_order ?? 0,
  };
}

function toZeroBrokerOutputUpdate(
  id: string,
  brokerID: string,
  output: CloudBrokerOutputUpdate,
) {
  return {
    id,
    brokerID,
    ...(output.name !== undefined ? { name: output.name } : {}),
    ...(output.output_type !== undefined
      ? { outputType: output.output_type }
      : {}),
    ...(output.config !== undefined ? { config: output.config } : {}),
    ...(output.enabled !== undefined ? { enabled: output.enabled } : {}),
    ...(output.sort_order !== undefined
      ? { sortOrder: output.sort_order }
      : {}),
  };
}

export const useBrokers = (studioId: string | undefined) => {
  const query = studioId
    ? appQueries.brokers.byStudio({ studioID: studioId })
    : undefined;
  const [data, details] = useQuery(query, { enabled: !!studioId });
  const brokers = ((data ?? []) as ZeroBroker[]).map(toLegacyBroker);
  return {
    data: brokers,
    isLoading:
      !!studioId && details.type !== "complete" && brokers.length === 0,
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(zeroErrorMessage(details.error))
        : null,
  };
};

export const useSingleBroker = (brokerId: string | undefined) => {
  const query = brokerId
    ? appQueries.brokers.byID({ id: brokerId })
    : undefined;
  const [data, details] = useQuery(query, { enabled: !!brokerId });
  return {
    data: data ? toLegacyBroker(data as ZeroBroker) : null,
    isLoading: !!brokerId && details.type !== "complete" && !data,
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(zeroErrorMessage(details.error))
        : null,
  };
};

export const useCreateBroker = () => {
  const zero = useZero() as ZeroMutateClient;
  const { isPending, runPending } = usePendingMutation();
  const run = (broker: CloudBrokerInsert) =>
    runPending(async () => {
      const input = toZeroBrokerInput(broker);
      const mutation = zero.mutate(appMutators.brokers.create(input));
      await mutation.client;
      await reportServerMutationFailure(
        mutation.server,
        "Broker create was rejected",
      );
      return toLegacyBroker(input);
    });
  return {
    mutate: (
      broker: CloudBrokerInsert,
      options?: MutationOptions<CloudBroker>,
    ) => {
      void run(broker)
        .then((data) => {
          toast.success("Broker created");
          options?.onSuccess?.(data);
        })
        .catch((err) => toast.error("Failed to create broker: " + err.message));
    },
    mutateAsync: run,
    isPending,
  };
};

export const useUpdateBroker = () => {
  const zero = useZero() as ZeroMutateClient;
  const { isPending, runPending } = usePendingMutation();
  const run = ({
    id,
    studioId,
    broker,
  }: {
    id: string;
    studioId: string;
    broker: CloudBrokerUpdate;
  }) =>
    runPending(async () => {
      const input = toZeroBrokerUpdate(id, studioId, broker);
      const mutation = zero.mutate(appMutators.brokers.update(input));
      await mutation.client;
      await reportServerMutationFailure(
        mutation.server,
        "Broker update was rejected",
      );
      return {
        data: toLegacyBroker({
          ...input,
          name: input.name ?? "",
          brokerType: input.brokerType ?? "",
          enabled: input.enabled ?? true,
        }),
        studioId,
      };
    });
  return {
    mutate: (
      input: { id: string; studioId: string; broker: CloudBrokerUpdate },
      options?: MutationOptions<{ data: CloudBroker; studioId: string }>,
    ) => {
      void run(input)
        .then((result) => {
          toast.success("Broker updated");
          options?.onSuccess?.(result);
        })
        .catch((err) => toast.error("Failed to update broker: " + err.message));
    },
    mutateAsync: run,
    isPending,
  };
};

export const useDeleteBroker = () => {
  const zero = useZero() as ZeroMutateClient;
  const { isPending, runPending } = usePendingMutation();
  const run = ({ id, studioId }: { id: string; studioId: string }) =>
    runPending(async () => {
      const mutation = zero.mutate(
        appMutators.brokers.delete({ id, studioID: studioId }),
      );
      await mutation.client;
      await reportServerMutationFailure(
        mutation.server,
        "Broker delete was rejected",
      );
      return { studioId };
    });
  return {
    mutate: (
      input: { id: string; studioId: string },
      options?: MutationOptions<{ studioId: string }>,
    ) => {
      void run(input)
        .then((result) => {
          toast.success("Broker deleted");
          options?.onSuccess?.(result);
        })
        .catch((err) => toast.error("Failed to delete broker: " + err.message));
    },
    mutateAsync: run,
    isPending,
  };
};

export const useBrokerOutputs = (brokerId: string | undefined) => {
  const query = brokerId
    ? appQueries.brokerOutputs.byBroker({ brokerID: brokerId })
    : undefined;
  const [data, details] = useQuery(query, { enabled: !!brokerId });
  const outputs = ((data ?? []) as ZeroBrokerOutput[]).map(
    toLegacyBrokerOutput,
  );
  return {
    data: outputs,
    isLoading:
      !!brokerId && details.type !== "complete" && outputs.length === 0,
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(zeroErrorMessage(details.error))
        : null,
  };
};

export const useCreateBrokerOutput = () => {
  const zero = useZero() as ZeroMutateClient;
  const { isPending, runPending } = usePendingMutation();
  const run = (output: CloudBrokerOutputInsert) =>
    runPending(async () => {
      const input = toZeroBrokerOutputInput(output);
      const mutation = zero.mutate(appMutators.brokerOutputs.create(input));
      await mutation.client;
      await reportServerMutationFailure(
        mutation.server,
        "Broker output create was rejected",
      );
      return toLegacyBrokerOutput(input);
    });
  return {
    mutate: (
      output: CloudBrokerOutputInsert,
      options?: MutationOptions<CloudBrokerOutput>,
    ) => {
      void run(output)
        .then((data) => {
          toast.success("Output created");
          options?.onSuccess?.(data);
        })
        .catch((err) => toast.error("Failed to create output: " + err.message));
    },
    mutateAsync: run,
    isPending,
  };
};

export const useUpdateBrokerOutput = () => {
  const zero = useZero() as ZeroMutateClient;
  const { isPending, runPending } = usePendingMutation();
  const run = ({
    id,
    brokerId,
    output,
  }: {
    id: string;
    brokerId: string;
    output: CloudBrokerOutputUpdate;
  }) =>
    runPending(async () => {
      const input = toZeroBrokerOutputUpdate(id, brokerId, output);
      const mutation = zero.mutate(appMutators.brokerOutputs.update(input));
      await mutation.client;
      await reportServerMutationFailure(
        mutation.server,
        "Broker output update was rejected",
      );
      return {
        data: toLegacyBrokerOutput({
          ...input,
          name: input.name ?? "",
          outputType: input.outputType ?? "",
          enabled: input.enabled ?? true,
          sortOrder: input.sortOrder ?? 0,
        }),
        brokerId,
      };
    });
  return {
    mutate: (
      input: { id: string; brokerId: string; output: CloudBrokerOutputUpdate },
      options?: MutationOptions<{ data: CloudBrokerOutput; brokerId: string }>,
    ) => {
      void run(input)
        .then((result) => {
          toast.success("Output updated");
          options?.onSuccess?.(result);
        })
        .catch((err) => toast.error("Failed to update output: " + err.message));
    },
    mutateAsync: run,
    isPending,
  };
};

export const useDeleteBrokerOutput = () => {
  const zero = useZero() as ZeroMutateClient;
  const { isPending, runPending } = usePendingMutation();
  const run = ({ id, brokerId }: { id: string; brokerId: string }) =>
    runPending(async () => {
      const mutation = zero.mutate(
        appMutators.brokerOutputs.delete({ id, brokerID: brokerId }),
      );
      await mutation.client;
      await reportServerMutationFailure(
        mutation.server,
        "Broker output delete was rejected",
      );
      return { brokerId };
    });
  return {
    mutate: (
      input: { id: string; brokerId: string },
      options?: MutationOptions<{ brokerId: string }>,
    ) => {
      void run(input)
        .then((result) => {
          toast.success("Output deleted");
          options?.onSuccess?.(result);
        })
        .catch((err) => toast.error("Failed to delete output: " + err.message));
    },
    mutateAsync: run,
    isPending,
  };
};
